<script>
import { mapActions, mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import KnowledgeBaseDropzone from './knowledgeBase/KnowledgeBaseDropzone.vue';
import StagedFileCard from './knowledgeBase/StagedFileCard.vue';
import { getOnboardingKbSession } from '../../../../helper/onboardingKbSession';

const HIGHLIGHT_MS = 1500;
const fileKey = file => `${file.name}|${file.size}|${file.lastModified}`;

export default {
  name: 'OnboardingKnowledgeBaseStep',
  components: {
    KnowledgeBaseDropzone,
    StagedFileCard,
  },
  data() {
    return {
      // Keys of the files just added, briefly highlighted so the user sees
      // their pick land.
      highlightedKeys: [],
    };
  },
  computed: {
    ...mapGetters('onboardingKnowledge', [
      'stagedFiles',
      'hasStagedFiles',
      'datasetId',
    ]),
    accountId() {
      return this.$route.params.accountId;
    },
    canContinue() {
      return this.hasStagedFiles;
    },
  },
  watch: {
    // The onboardingKnowledge Vuex module is registered once for the whole
    // SPA session, not per-account — without this, files staged while
    // onboarding one account would still be sitting in state (and usable)
    // after navigating to another account's onboarding flow.
    accountId: {
      immediate: true,
      handler() {
        this.resetIfAccountChanged();
      },
    },
  },
  mounted() {
    // This account already has a knowledge base in progress (still in the
    // store, or remembered across a refresh). Picking files again here would
    // create a second one — the Knowledge API accepts duplicate names and
    // each copy indexes and bills separately — so go back to where it is
    // being processed instead.
    if (this.datasetId || getOnboardingKbSession(this.accountId)) {
      this.$router.replace({
        name: 'onboarding_knowledge_options',
        params: { accountId: this.accountId },
      });
    }
  },
  beforeDestroy() {
    clearTimeout(this.highlightTimer);
  },
  methods: {
    fileKey,
    ...mapActions('onboardingKnowledge', [
      'stageFiles',
      'removeStagedFileAt',
      'resetIfAccountChanged',
    ]),
    async handleFilesSelected(files) {
      const { added, duplicates } = await this.stageFiles(files);
      duplicates.forEach(file =>
        useAlert(
          this.$t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.ERROR_DUPLICATE', {
            name: file.name,
          })
        )
      );
      if (!added.length) return;
      this.highlightedKeys = added.map(fileKey);
      clearTimeout(this.highlightTimer);
      this.highlightTimer = setTimeout(() => {
        this.highlightedKeys = [];
      }, HIGHLIGHT_MS);
    },
    handleValidationError(message) {
      useAlert(message);
    },
    handleContinue() {
      if (!this.canContinue) return;
      this.$router.push({
        name: 'onboarding_knowledge_options',
        params: { accountId: this.accountId },
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-4 p-6 md:p-10 w-full h-full">
    <h2 class="text-xl font-medium text-slate-900 dark:text-slate-100">
      {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.HEADING') }}
    </h2>
    <p class="text-sm text-slate-500 dark:text-slate-400">
      {{ $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.SUBHEADING') }}
    </p>

    <KnowledgeBaseDropzone
      :compact="hasStagedFiles"
      @filesSelected="handleFilesSelected"
      @validationError="handleValidationError"
    />

    <div v-if="stagedFiles.length" class="flex flex-col gap-3">
      <span
        class="text-sm font-medium text-slate-700 dark:text-slate-200"
        data-testid="kb_staged_files_label"
      >
        {{
          $t('ONBOARDING_SHELL.STEPS.KNOWLEDGE_BASE.SELECTED_FILES_LABEL', {
            count: stagedFiles.length,
          })
        }}
      </span>
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <StagedFileCard
          v-for="(file, index) in stagedFiles"
          :key="fileKey(file)"
          :file="file"
          :highlighted="highlightedKeys.includes(fileKey(file))"
          @remove="removeStagedFileAt(index)"
        />
      </div>
    </div>

    <div class="flex-1" />

    <div class="flex justify-end">
      <woot-button :disabled="!canContinue" @click="handleContinue">
        {{ $t('ONBOARDING_SHELL.CONTINUE_BUTTON') }}
      </woot-button>
    </div>
  </div>
</template>
